import {
  combineCarts,
  filterOutDeliveryPromotions,
  transformFreeItemPromotionToDiscount,
  transformItemPromotionToDiscount,
  transformOrderPromotionToDiscount,
} from "../features/promotions/helpers/utils";
import {
  FIXED_SURCHARGE,
  ORDER_TYPE_DELIVERY,
  SCHEDULE_BUFFER_TIME_IN_MINS,
} from "../helpers/constants";
import { findScheduledTime } from "./timeCalculations";
import { calculateAmountsAndBreakdowns } from "./itemCalculations";
import { transformTaxBreakdown } from "./utils";

const orderSurcharges = (
  surcharges: any,
  taxes: any,
  orderType: any,
  area: any,
  deliveryChargeId: any,
) => {
  surcharges = surcharges.filter((charge: any) => {
    if (orderType == ORDER_TYPE_DELIVERY) {
      return charge.applicable_on_weborder;
    } else {
      return charge.applicable_on_takeaway && charge.id != deliveryChargeId;
    }
  });
  return (
    surcharges.map((surcharge: any) => {
      let surchargeValue;
      let isDeliveryCharge = surcharge.id == deliveryChargeId;
      if (isDeliveryCharge) {
        surchargeValue = area.delivery_charge == null ? surcharge.value : area.delivery_charge;
      } else {
        surchargeValue = surcharge.value;
      }
      let surchargeItem = {
        id: surcharge.id,
        name: surcharge.name,
        surcharge_type: (surcharge.type == FIXED_SURCHARGE ? 2 : 1),
        rate: parseFloat(surchargeValue),
        is_taxable: (surcharge.taxes.length > 0),
        is_delivery_charge: isDeliveryCharge
      }
      if (surcharge.taxes.length) {
        let taxId = surcharge.taxes[0].taxes[0];
        let tax = taxes.find((tax: any) => { return (tax.id == taxId) })
        let surchargeTax = {
          tax_rate_id: taxId,
          tax_rate: parseFloat(tax.value)
        }
        Object.assign(surchargeItem, surchargeTax);
      }
      return surchargeItem;
    })
  );
}

const orderItems = (items: any, appliedPromotions:any[] = []) => {
  return (
    items.map((item: any) => {
      let itemDetails =  {
        id: item.id,
        quantity: parseFloat(item.count),
        note: '',
        free_item: false,
        modifiers: [] as any,
        name: item.name,
        unit_price: parseFloat(item.unit_price),
        sub_total: parseFloat(item.sub_total),
        image_url: item.image_url,
        translations: item.translations,
        discount: {} as any,
        item_code: item.item_code ?? null,
      }
      item.groups.map((group: any) => {
        group.items.map((modifier: any) => {
          if (modifier.selected) {
            let count = 1;
            if ("count" in modifier) {
              count = modifier.count;
            }
            itemDetails.modifiers.push(
              {
                id: modifier.id,
                group_id: group.id,
                quantity: count,
                name: modifier.name,
                unit_price: parseFloat(modifier.unit_price),
                sub_total: count * parseFloat(modifier.unit_price),
                items_modifer_group_item_id: modifier.modifier_id,
                translations: modifier.translations,
              }
            )
          }
        })
      });
      if (appliedPromotions.length && item.applied_promotion_id) {
        const appliedPromotion = appliedPromotions.find((promotion: any) => {
          return promotion.id === item.applied_promotion_id;
        });
        if (item.is_free_item) {
          let itemDiscount = transformFreeItemPromotionToDiscount(
            appliedPromotion,
            item
          );
          if (itemDiscount) {
            itemDetails.sub_total += itemDiscount.amount;
            itemDetails.discount = {...itemDiscount};
          }
        } else {
          let itemDiscount = transformItemPromotionToDiscount(
            appliedPromotion,
            item
          );
          if (itemDiscount) {
            itemDetails.sub_total += itemDiscount.amount;
            itemDetails.discount = {...itemDiscount};
          }
        }
      }
      return itemDetails;
    })
  );
}

const baseOrderPayload = (state: any, props: any, paymentMode?: any) => {
  let cartItems = props.cart?.items ?? [];
  if (props.promotionAppliedCart) {
    cartItems = combineCarts(props.promotionAppliedCart, props.freeItemsCart);
  }
  
  // Recalculate totals from cartItems to ensure consistency
  // This prevents race conditions where props.cart totals don't match cartItems
  const amountBreakdowns = calculateAmountsAndBreakdowns(
    cartItems,
    props.menu,
    props.orderType,
    props.discount,
    props.loyalty?.redeemedPoints || null,
    props.selectedArea || {},
    props.outlet.delivery_charge_id || null,
    props.riderTip || null,
    props.appliedOrderPromotion || []
  );
  
  // Collect promotion IDs from cart items and merge with appliedPromotions
  // This ensures free item promotions are included even if not in Redux state
  const promotionIdsFromCart = cartItems
    .filter((item: any) => item.applied_promotion_id)
    .map((item: any) => item.applied_promotion_id);
  
  const uniquePromotionIds = Array.from(new Set(promotionIdsFromCart));
  
  // Find promotion objects from availablePromotions for any IDs in cart
  const availablePromotions = props.availablePromotions || [];
  const promotionsFromCart = uniquePromotionIds
    .map((id: number) => availablePromotions.find((promo: any) => promo.id === id))
    .filter((promo: any) => promo !== undefined);
  
  // Merge with existing appliedPromotions, avoiding duplicates
  const mergedAppliedPromotions = [...(props.appliedPromotions || [])];
  promotionsFromCart.forEach((promo: any) => {
    if (!mergedAppliedPromotions.find((p: any) => p.id === promo.id)) {
      mergedAppliedPromotions.push(promo);
    }
  });
  
  const orderPromotions = props.appliedOrderPromotion
  let orderPromotionDiscount:any = null;
  if (orderPromotions.length) {
    orderPromotionDiscount = transformOrderPromotionToDiscount(
      orderPromotions[0],
      amountBreakdowns.subTotal,
      amountBreakdowns.surchargeSubTotal,
      amountBreakdowns.surchargeBreakDown
    );
  }

  const isStoreClosedAndOrderAllowedWhenClosed =
    !props.storeOpen && props.allowOrderWhenClosed;
  let storeCloseDefaultEta = null;
  if (
    isStoreClosedAndOrderAllowedWhenClosed &&
    state.deliveryEta &&
    props.company.default_eta
  ) {
    storeCloseDefaultEta = findScheduledTime(
      state.deliveryEta,
      SCHEDULE_BUFFER_TIME_IN_MINS,
      props.company.default_eta
    );
  }
  const taxBreakdown = transformTaxBreakdown(amountBreakdowns.taxBreakdown);
  let payload: any = {
    online_company_id: props.company.id,
    online_location_id: props.outlet.id,
    scheduled_at: state.scheduledTime
      ? state.scheduledTime
      : isStoreClosedAndOrderAllowedWhenClosed
      ? storeCloseDefaultEta
      : null,
    default_eta: state.deliveryEta,
    online_delivery_eta: state.minimumEtaMinutes,
    note: {
      user: state.orderNotes || "",
      meta: state.metaNotes || "",
    },
    surcharges: orderSurcharges(
      props.menu.surcharges,
      props.menu.taxes,
      props.orderType,
      props.selectedArea,
      props.outlet.delivery_charge_id
    ) as any,
    items: orderItems(cartItems, mergedAppliedPromotions),
    delivery_address_id: state.selectedAddress.id,
    delivery_area: props.selectedArea?.name,
    complete_address: state.selectedAddress?.lines,
    discounts:
      props.discount && Object.keys(props.discount).length
        ? [
            {
              id: props.discount.discount_id,
              name: props.discount.discount_name,
              type: props.discount.discount_type.toLowerCase(),
              discount_type:
                props.discount.discount_type == "PERCENTAGE" ? 1 : 2,
              on_total: props.discount.on_total,
              rate: props.discount.discount_rate,
              amount: props.discount.discountedAmount,
            },
          ]
        : props.appliedOrderPromotion &&
          props.appliedOrderPromotion.length &&
          orderPromotionDiscount
        ? [orderPromotionDiscount]
        : [],
    redeemed_loyalty: 0,
    remaining_loyalty: 0,
    earned_loyalty: 0,
    has_loyalty_data: false,
    loyalty_as_discount: props.loyaltyConfig?.is_discount || false,
    sub_total: amountBreakdowns.subTotal,
    sub_total_after_discount: amountBreakdowns.subTotalAfterDiscount,
    sub_total_tax_after_discount: amountBreakdowns.subTotalTaxAfterDiscount,
    surcharge_sub_total: amountBreakdowns.surchargeSubTotal,
    surcharges_breakdown: amountBreakdowns.surchargeBreakDown,
    tax_total: amountBreakdowns.taxTotal,
    tax_group_breakdown: taxBreakdown,
    total_amount: amountBreakdowns.total,
    order_type: props.session.order_type,
    customer_id: props.user.sapaad_customer_id,
    cartItems: cartItems,
    is_tax_inclusive: props.menu.is_tax_inclusive,
    pickup_mode: "",
    user_name: state.userName,
    user_email: state.userEmail,
    currency: props.company.currency,
    applied_promotions: mergedAppliedPromotions,
    applied_promo_code_details: props.appliedPromoCodeDetails || null,
    total_before_loyalty: amountBreakdowns.totalBeforeLoyalty,
    generic_standalone_integration:
      paymentMode?.generic_standalone_integration || false,
    generic_integration: paymentMode?.generic_integration || false,
  };
  if (
    props.loyaltyConfig && props.loyaltyConfig.value_for_points &&
    props.loyalty && Object.keys(props.loyalty).length
  ) {
    payload.has_loyalty_data = true;
    payload.loyalty_value = props.loyaltyConfig.value_for_points;
    if (props.loyalty.redeemedPoints) {
      payload.redeemed_loyalty = props.loyalty.redeemedPoints;
      payload.remaining_loyalty =
        parseFloat(props.loyalty.customer_loyalty || 0) -
        parseFloat(props.loyalty.redeemedPoints);
    } else {
      payload.remaining_loyalty = parseFloat(props.loyalty.customer_loyalty || 0);
    }
  }
  if (props.orderType == "pickup") {
    payload.pickup_mode = state.pickupMode;
  } else {
    payload.source_coordinates = {
      latitude: props.outlet.latitude,
      longitude: props.outlet.longitude,
    };
    payload.destination_coordinates = {
      latitude: state.selectedAddress?.latitude,
      longitude: state.selectedAddress?.longitude,
    }
  }

  if (props.riderTip) {
    payload.tips = parseFloat(props.riderTip || 0);
  }

  return payload;
};

const orderPayload = (state: any, props: any) => {
  let paymentMode = state.paymentTypes.find((pt: any) => {
    return pt.payment_type === state.selectedPaymentType;
  });

  const payload = baseOrderPayload(state, props, paymentMode);

  let paymentDetails: any = {
    id: paymentMode.sapaad_payment_type_id,
    payment_type_id: paymentMode.sapaad_payment_type_id,
    payment_type: paymentMode.payment_type,
    amount: parseFloat(payload.total_amount),
    name: state.selectedPaymentTypeTitle,
    tips: parseFloat(props.riderTip || 0),
  };

  return (
    Object.assign(payload, { payment: paymentDetails })
  );
}

export {
  baseOrderPayload,
  orderPayload,
  orderItems,
  orderSurcharges
};
